
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { BrainCircuit } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { SermonFormData } from "@/hooks/sermon-plans/types";
import { useBraceletCheck } from "@/hooks/useBraceletCheck";
import { supabase } from "@/integrations/supabase/client";

interface CongregationInsightButtonProps {
  form: UseFormReturn<SermonFormData>;
}

export function CongregationInsightButton({ form }: CongregationInsightButtonProps) {
  const { toast } = useToast();
  const { braceletId } = useBraceletCheck();
  const [showButton, setShowButton] = useState(false);
  
  // Check if the bracelet ID is associated with a specific congregation
  useEffect(() => {
    const checkBraceletCongregation = async () => {
      if (!braceletId) {
        setShowButton(false);
        return;
      }

      try {
        // Fetch the bracelet's church code
        const { data: bracelet, error } = await supabase
          .from('bracelets')
          .select('church_code')
          .eq('referral_code', braceletId)
          .single();

        if (error || !bracelet) {
          setShowButton(false);
          return;
        }

        // Only show for TEST COMM (church code 001)
        setShowButton(bracelet.church_code === '001');
      } catch (err) {
        console.error('Error checking bracelet congregation:', err);
        setShowButton(false);
      }
    };

    checkBraceletCongregation();
  }, [braceletId]);

  const handleCongregationFocus = () => {
    // In a real implementation, this would analyze congregation data
    toast({
      title: "Congregation Insights",
      description: "Analyzing congregation data for relevant sermon content...",
    });

    // Simulate congregation data analysis with example values
    setTimeout(() => {
      form.setValue("theme", "Community Support and Connection");
      form.setValue("scripture_reference", "1 Corinthians 12:12-27");
      form.setValue("key_points", "Unity in diversity\nSupporting one another\nSharing burdens as a community\nRecognizing everyone's gifts");
      
      toast({
        title: "Insights Applied",
        description: "Congregation data analysis complete. Form has been updated with insights.",
      });
    }, 1500);
  };

  if (!showButton) return null;

  return (
    <Button 
      onClick={handleCongregationFocus}
      className="mx-6 mb-4 bg-purple-600 hover:bg-purple-700 flex items-center"
    >
      <BrainCircuit className="mr-2 h-5 w-5" />
      Use congregation data for sermon insights
    </Button>
  );
}
